import React from "react";
import { formatTimestamp } from "../../../utils/helper";

interface FieldChange {
    fieldName: string;
    displayName: string;
    oldValue: string | null;
    newValue: string | null;
}

export interface PlaFeedAuditLogItem {
    historyId?: number;
    msn: string;
    action: string;
    batchId?: string;
    changedBy?: string;
    traceId?: string;
    historyTimestamp?: string | number;
    changes: FieldChange[];
}

interface PlaFeedAuditLogProps {
    logs?: PlaFeedAuditLogItem[];
    isLoading?: boolean;
    msn?: string;
}

const ACTION_STYLES: Record<string, string> = {
    INSERT: "bg-green-100 text-green-700",
    UPDATE: "bg-amber-100 text-amber-700",
    DELETE: "bg-red-100 text-red-700",
};

const ACTION_LABELS: Record<string, string> = {
    INSERT: "Created",
    UPDATE: "Updated",
    DELETE: "Deleted",
};

export const timeAgo = (timestamp: any) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const timeMs = date.getTime();
    if (isNaN(timeMs)) return "";
    const diff = Date.now() - timeMs;
    if (diff < 0) return "just now";
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
};

const ValuePill: React.FC<{ value: any; field?: string }> = ({ value, field }) => {
    if (value === null || value === undefined || value === "") {
        return <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-400 text-[11px]">—</span>;
    }

    const strVal = String(value).trim();

    // Boolean flags (Shopping Ads, Active flags, etc.)
    if (strVal === "true" || strVal === "false") {
        const isTrue = strVal === "true";
        return (
            <span
                className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium border ${
                    isTrue
                        ? "bg-green-100 text-green-800 border-green-200"
                        : "bg-red-100 text-red-800 border-red-200"
                }`}
            >
                {isTrue ? "True" : "False"}
            </span>
        );
    }

    // Country codes
    if (field === "countryCode") {
        const isIndia = strVal === "356";
        return (
            <span
                className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium border ${
                    isIndia
                        ? "bg-teal-100 text-teal-800 border-teal-200"
                        : "bg-indigo-100 text-indigo-800 border-indigo-200"
                }`}
            >
                {isIndia ? "India" : strVal === "784" ? "UAE" : strVal}
            </span>
        );
    }

    // Stock availability
    if (field === "availability") {
        const inStock = strVal.toLowerCase() === "in stock";
        return (
            <span
                className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium border ${
                    inStock
                        ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                        : "bg-amber-100 text-amber-800 border-amber-200"
                }`}
            >
                {strVal}
            </span>
        );
    }

    return (
        <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[11px] font-normal break-all">
            {strVal}
        </span>
    );
};

const EXCLUDED_DIFF_FIELDS = new Set([
    "brand",
    "title",
    "description",
    "link",
    "imageLink",
    "price",
    "salePrice",
    "availability",
    "displayAdsId",
    "displayAdsTitle",
    "displayAdsLink",
    "adsGrouping",
    "adsLabels",
]);

export const summarizePlaLog = (log?: PlaFeedAuditLogItem) => {
    if (!log) return "";
    const isInsert = (log.action || "").toUpperCase() === "INSERT";
    if (isInsert) return "Created";
    const relevantChanges = (log.changes || []).filter(
        (c) => !EXCLUDED_DIFF_FIELDS.has(c.fieldName)
    );
    if (!relevantChanges.length) return "No field changes";
    const names = relevantChanges.map((c) => c.displayName || c.fieldName);
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
};

const PlaFeedAuditLog: React.FC<PlaFeedAuditLogProps> = ({ logs, isLoading, msn }) => {
    if (isLoading) {
        return (
            <div className="py-3 flex items-center gap-2 text-xs text-gray-500">
                <span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                <span>Loading change history for {msn || "item"}...</span>
            </div>
        );
    }

    const sortedLogs = [...(logs || [])].sort((a, b) => {
        const timeA = new Date(a?.historyTimestamp || 0).getTime();
        const timeB = new Date(b?.historyTimestamp || 0).getTime();
        return timeB - timeA;
    });

    if (!sortedLogs.length) {
        return <p className="text-xs text-gray-500 py-2">No change history recorded for this MSN.</p>;
    }

    return (
        <div className="py-2">
            <p className="text-[11px] font-semibold text-[#43425D] uppercase tracking-wide mb-2">
                Change history ({sortedLogs.length})
            </p>
            <ul className="border-l-2 border-gray-200 ml-1.5">
                {sortedLogs.map((log, index) => {
                    const action = log?.action?.toUpperCase() || "UPDATE";
                    const isInsert = action === "INSERT";
                    const rawChanges = log?.changes || [];
                    const changes = rawChanges.filter((c) => !EXCLUDED_DIFF_FIELDS.has(c.fieldName));

                    return (
                        <li key={log?.historyId || index} className="relative pl-4 pb-3 last:pb-0">
                            {/* Visual bead */}
                            <span
                                className={`absolute -left-[5px] top-1.5 w-2 h-2 rounded-full ${
                                    isInsert ? "bg-green-600" : "bg-amber-500"
                                }`}
                            />

                            {/* Event header */}
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                        ACTION_STYLES[action] || "bg-gray-100 text-gray-700"
                                    }`}
                                >
                                    {ACTION_LABELS[action] || action}
                                </span>
                                <span className="text-xs text-[#3F3F3F] font-medium">
                                    {log?.changedBy || "System"}
                                </span>
                                <span
                                    className="text-[11px] text-gray-500"
                                    title={formatTimestamp(log?.historyTimestamp)}
                                >
                                    {timeAgo(log?.historyTimestamp)} · {formatTimestamp(log?.historyTimestamp)}
                                </span>
                                {log?.batchId ? (
                                    <span
                                        className="text-[10px] text-gray-400 truncate max-w-[150px]"
                                        title={`Batch: ${log.batchId}`}
                                    >
                                        batch: {log.batchId.slice(0, 8)}...
                                    </span>
                                ) : null}
                            </div>

                            {/* Field diffs */}
                            <div className="mt-1.5">
                                {isInsert ? (
                                    <>
                                        <p className="text-[11px] text-gray-500 mb-1">
                                            Record added with initial feed settings:
                                        </p>
                                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                                            {changes.map((field, cIdx) => (
                                                <span
                                                    key={cIdx}
                                                    className="flex items-center gap-1 text-[11px] text-[#3F3F3F]"
                                                >
                                                    <span className="text-gray-500 font-medium">
                                                        {field.displayName || field.fieldName}:
                                                    </span>
                                                    <ValuePill
                                                        value={field.newValue}
                                                        field={field.fieldName}
                                                    />
                                                </span>
                                            ))}
                                        </div>
                                    </>
                                ) : changes.length > 0 ? (
                                    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                                        {changes.map((change, cIdx) => (
                                            <span
                                                key={cIdx}
                                                className="flex items-center gap-1 text-[11px] text-[#3F3F3F] bg-white px-1.5 py-0.5 rounded border border-gray-100 shadow-sm"
                                            >
                                                <span className="text-gray-500 font-medium">
                                                    {change.displayName || change.fieldName}:
                                                </span>
                                                {change.oldValue !== null &&
                                                change.oldValue !== undefined &&
                                                change.oldValue !== "" ? (
                                                    <>
                                                        <ValuePill
                                                            value={change.oldValue}
                                                            field={change.fieldName}
                                                        />
                                                        <span className="text-gray-400">→</span>
                                                    </>
                                                ) : null}
                                                <ValuePill
                                                    value={change.newValue}
                                                    field={change.fieldName}
                                                />
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-[11px] text-gray-400 italic">
                                        Re-uploaded with no field changes.
                                    </p>
                                )}
                            </div>

                            {/* Trace ID */}
                            {log?.traceId ? (
                                <p className="text-[10px] text-gray-400 mt-1">Trace: {log.traceId}</p>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default PlaFeedAuditLog;
